import * as React from 'react';

import {
  MenuAccordionCheckboxItem,
  MenuAccordionItem,
  MenuCheckboxItem,
  MenuRadioItem,
} from '@creditornot/cb-menu';
import { findChildren, findDescendants } from 'common/utils/hierarchy';

import { TreeSelectOption } from './types';

type Props = {
  option: TreeSelectOption;
  descendants: TreeSelectOption[];
  onChange: (option: TreeSelectOption) => void;
  expand?: boolean;
  multiselect?: boolean;
};

export function Option({
  option: currentOption,
  onChange,
  expand,
  descendants,
  multiselect,
}: Props) {
  const hasSomeDescendantsChecked = descendants.some(({ checked }) => checked);
  const partiallyChecked = !currentOption.checked && hasSomeDescendantsChecked;

  const [open, setOpen] = React.useState(expand);
  const children = findChildren(currentOption.value, descendants);

  function handleAccordionClick() {
    setOpen((current) => !current);
  }

  const optionId = currentOption.value;

  if (!children.length) {
    if (multiselect) {
      return (
        <MenuCheckboxItem
          checked={currentOption.checked}
          value={currentOption.value}
          id={optionId}
          onClick={() => onChange(currentOption)}
          role="checkbox"
        >
          {currentOption.label}
        </MenuCheckboxItem>
      );
    }

    return (
      <MenuRadioItem
        checked={currentOption.checked}
        value={currentOption.value}
        id={optionId}
        data-test-id={optionId}
        onChange={() => onChange(currentOption)}
      >
        {currentOption.label}
      </MenuRadioItem>
    );
  }

  if (!multiselect) {
    return (
      <MenuAccordionItem
        open={open}
        onClick={handleAccordionClick}
        accordionContent={children.map((childOption) => (
          <Option
            descendants={findDescendants(childOption.value, descendants)}
            expand={expand}
            key={childOption.value}
            option={childOption}
            onChange={onChange}
          />
        ))}
      >
        <span>{currentOption.label}</span>
      </MenuAccordionItem>
    );
  }

  return (
    <MenuAccordionCheckboxItem
      open={open}
      onClick={handleAccordionClick}
      accordionContent={children.map((childOption) => (
        <Option
          descendants={findDescendants(childOption.value, descendants)}
          expand={expand}
          key={childOption.value}
          option={childOption}
          onChange={onChange}
          multiselect={multiselect}
        />
      ))}
      checkboxProps={{
        id: optionId,
        checked: currentOption.checked,
        partiallyChecked,
        onChange: () => onChange(currentOption),
        title: currentOption.label,
        value: currentOption.value,
      }}
    >
      <span>{currentOption.label}</span>
    </MenuAccordionCheckboxItem>
  );
}
